import React, { useMemo, useState, useEffect } from 'react';
import { 
  Tabs, 
  Form, 
  Button, 
  Typography, 
  Space, 
  Select, 
  Switch,
  message, 
  Tag, 
  Popconfirm,
  Avatar,
  Badge,
  Tooltip
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined,
  PlusOutlined, 
  DeleteOutlined, 
  TeamOutlined,
  SettingOutlined,
  BuildOutlined,
  CreditCardOutlined,
  CrownOutlined,
  SafetyOutlined,
  SlidersOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../helpers/api/apiConfig';
import { isMasterUser, getUserRoleCode } from '../helpers/accessControl';
import ProfileTab from '../components/Configuration/ProfileTab';
import ApiTokensTab from '../components/Configuration/ApiTokensTab';
import UserManagementTab from '../components/Configuration/UserManagementTab';
import CompanyManagementTab from '../components/Configuration/CompanyManagementTab';
import RangesTab from '../components/Configuration/RangesTab';
import SubscriptionTab from '../components/Configuration/SubscriptionTab';
import NewCompanyTab from '../components/Configuration/NewCompanyTab';
import type { CompanyMember, Invitation, Role } from '../components/Configuration/types';
import '../style/Configuration.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const Configuration: React.FC = () => {
  const { currentUser: user, logoutUser: logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [companyMembers, setCompanyMembers] = useState<CompanyMember[]>([]);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteForm] = Form.useForm();
  const buildAuthHeaders = (includeContentType = true) => {
    const token = localStorage.getItem('accessToken');
    const csrfToken = localStorage.getItem('csrfToken');
    return {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };
  };
  const handleAuthMismatch = async (response: Response, errorData: any) => {
    if (response.status === 401 && errorData?.error === 'auth_mismatch') {
      message.error('Session mismatch detected. Please log in again.');
      await logout();
      return true;
    }
    return false;
  };

  // Use new access control system
  const isMaster = isMasterUser(user);
  const userRoleCode = getUserRoleCode(user);
  const isAdmin = userRoleCode === 'admin' || isMaster;
  const currentCompanyId = user?.currentCompanyId || user?.companyId;
  const currentMembership = user?.companyMemberships?.find(
    (membership) => membership.company.id === currentCompanyId
  ) || user?.companyMemberships?.[0];
  const effectiveRole = user?.currentCompanyRole || currentMembership?.role || user?.role;
  const effectiveRoleCode = effectiveRole?.code || '';
  const effectiveRoleLabel = effectiveRole?.description || 'User';
  const roleTagColor = effectiveRoleCode === 'admin' ? 'blue' : 'green';
  const effectiveCompany =
    currentMembership?.company ||
    user?.associatedCompanies?.find((company) => company.id === currentCompanyId) ||
    user?.associatedCompanies?.[0];
  const effectiveCompanyName = effectiveCompany?.name || 'No Company';

  useEffect(() => {
    if (isAdmin || isMaster) {
      fetchInvitations();
      fetchRoles();
      fetchCompanyUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isMaster]);

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user-invitations/company`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      } else {
        const errorData = await response.json();
        if (await handleAuthMismatch(response, errorData)) return;
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/roles`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data.filter((role: Role) => role.code !== 'master')); // Exclude master role
      } else {
        const errorData = await response.json();
        if (await handleAuthMismatch(response, errorData)) return;
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      const currentCompanyId = user?.currentCompanyId || user?.companyId;
      
      if (!currentCompanyId) {
        console.error('No company ID available');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/memberships/company/${currentCompanyId}/members`, {
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCompanyMembers(data.members || []);
      } else {
        const errorData = await response.json();
        if (await handleAuthMismatch(response, errorData)) return;
      }
    } catch (error) {
      console.error('Error fetching company members:', error);
    }
  };

  const handleInviteUser = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user-invitations/send`, {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify(values),
      });

      if (response.ok) {
        message.success('Invitation sent successfully!');
        setInviteModalVisible(false);
        inviteForm.resetFields();
        fetchInvitations();
      } else {
        const error = await response.json();
        if (await handleAuthMismatch(response, error)) return;
        message.error(error.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      message.error('Failed to send invitation');
    }
    setLoading(false);
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user-invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        message.success('Invitation revoked successfully');
        fetchInvitations();
      } else {
        const errorData = await response.json();
        if (await handleAuthMismatch(response, errorData)) return;
        message.error('Failed to revoke invitation');
      }
    } catch (error) {
      console.error('Error revoking invitation:', error);
      message.error('Failed to revoke invitation');
    }
  };

  const getRoleIcon = (roleCode: string) => {
    switch (roleCode) {
      case 'master':
        return <CrownOutlined style={{ color: '#gold' }} />;
      case 'admin':
        return <SafetyOutlined style={{ color: '#1890ff' }} />;
      default:
        return <UserOutlined style={{ color: '#52c41a' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'processing';
      case 'accepted':
        return 'success';
      case 'expired':
        return 'error';
      case 'revoked':
        return 'default';
      default:
        return 'default';
    }
  };

  const invitationColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <Space>
          <MailOutlined />
          {email}
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (_: string, record: Invitation) => (
        <Space>
          <Text>{record.role}</Text>
          {record.isMaster && <Tag color="gold">Master</Tag>}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Invited At',
      dataIndex: 'invitedAt',
      key: 'invitedAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Expires',
      dataIndex: 'validUntil',
      key: 'validUntil',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Invitation) => (
        <Space>
          {record.status === 'pending' && record.isValid && (
            <Popconfirm
              title="Are you sure you want to revoke this invitation?"
              onConfirm={() => handleRevokeInvitation(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
              >
                Revoke
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleChangeRole = async (membershipId: number, newRoleId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/memberships/membership/${membershipId}/role`, {
        method: 'PUT',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ newRoleId }),
      });

      if (response.ok) {
        message.success('User role updated successfully');
        fetchCompanyUsers();
      } else {
        const error = await response.json();
        if (await handleAuthMismatch(response, error)) return;
        message.error(error.message || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Error changing role:', error);
      message.error('Failed to update user role');
    }
  };

  const handleToggleMaster = async (userId: number, nextIsMaster: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}/master`, {
        method: 'PATCH',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({ isMaster: nextIsMaster }),
      });

      if (response.ok) {
        message.success('Master access updated');
        fetchCompanyUsers();
      } else {
        const error = await response.json();
        if (await handleAuthMismatch(response, error)) return;
        message.error(error.message || 'Failed to update master access');
      }
    } catch (error) {
      console.error('Error updating master access:', error);
      message.error('Failed to update master access');
    }
  };

  const handleRemoveMember = async (membershipId: number, email: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/memberships/membership/${membershipId}`, {
        method: 'DELETE',
        headers: {
          ...buildAuthHeaders(),
        },
        credentials: 'include',
      });

      if (response.ok) {
        message.success(`Removed ${email} from company`);
        fetchCompanyUsers();
      } else {
        const error = await response.json();
        if (await handleAuthMismatch(response, error)) return;
        message.error(error.message || 'Failed to remove user');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      message.error('Failed to remove user');
    }
  };

  const userColumns = (() => {
    const columns = [
      {
        title: 'User',
        key: 'user',
        render: (record: CompanyMember) => (
          <Space>
            <Avatar icon={<UserOutlined />} />
            <div>
              <div>{record.user.email}</div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.role.description}
              </Text>
            </div>
          </Space>
        ),
      },
      {
        title: 'Role',
        key: 'role',
        render: (record: CompanyMember) => (
          <Space>
            {getRoleIcon(record.role.code)}
            <Tag color={record.role.code === 'admin' ? 'blue' : 'green'}>
              {record.role.description}
            </Tag>
            {isMaster && record.user.isMaster && <Tag color="gold">Master</Tag>}
          </Space>
        ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (record: CompanyMember) => (
          <Badge 
            status={record.status === 'active' ? 'success' : 'default'} 
            text={record.status.toUpperCase()} 
          />
        ),
      },
      {
        title: 'Joined',
        key: 'joinedAt',
        render: (record: CompanyMember) => (
          new Date(record.joinedAt).toLocaleDateString()
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (record: CompanyMember) => {
          // Don't allow removing or changing role of current user
          const isCurrentUser = record.user.id === user?.id;
          
          return (
            <Space>
              {!isCurrentUser && (
                <>
                  <Select
                    value={record.role.id}
                    onChange={(newRoleId) => handleChangeRole(record.membershipId, newRoleId)}
                    style={{ width: 130 }}
                    size="small"
                  >
                    {roles.map(role => (
                      <Option key={role.id} value={role.id}>
                        {role.description}
                      </Option>
                    ))}
                  </Select>
                  <Popconfirm
                    title={`Remove ${record.user.email} from company?`}
                    description="This action cannot be undone."
                    onConfirm={() => handleRemoveMember(record.membershipId, record.user.email)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                    >
                      Remove
                    </Button>
                  </Popconfirm>
                </>
              )}
              {isCurrentUser && (
                <Text type="secondary" style={{ fontSize: '12px' }}>You</Text>
              )}
            </Space>
          );
        },
      },
    ];

    if (isMaster) {
      columns.splice(2, 0, {
        title: 'Master',
        key: 'master',
        render: (record: CompanyMember) => {
          const isCurrentUser = record.user.id === user?.id;

          return (
            <Tooltip title={isCurrentUser ? 'You cannot change your own master access' : 'Toggle master access'}>
              <Switch
                checked={record.user.isMaster === true}
                size="small"
                disabled={isCurrentUser}
                onChange={(checked) => handleToggleMaster(record.user.id, checked)}
              />
            </Tooltip>
          );
        },
      });
    }

    return columns;
  })();

  const tabFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tab') || 'profile';
  }, [location.search]);

  const [activeTab, setActiveTab] = useState(tabFromQuery);

  useEffect(() => {
    setActiveTab(tabFromQuery);
  }, [tabFromQuery]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    navigate(`/configuration?tab=${key}`, { replace: true });
  };

  return (
    <div className="configuration-page">
      <div className="configuration-header">
        <Title level={2} style={{ margin: 0 }}>
          <Space>
            <SettingOutlined />
            Configuration
          </Space>
        </Title>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange} type="card" className="configuration-tabs">
        <TabPane 
          tab={
            <Space>
              <UserOutlined />
              Profile
            </Space>
          } 
          key="profile"
        >
          <div className="configuration-tab-content configuration-tab-profile">
            <ProfileTab
              email={user?.email}
              avatarUrl={user?.avatarUrl}
              isMaster={isMaster}
              effectiveRoleCode={effectiveRoleCode}
              effectiveRoleLabel={effectiveRoleLabel}
              roleTagColor={roleTagColor}
              effectiveCompanyName={effectiveCompanyName}
              onLogout={logout}
              getRoleIcon={getRoleIcon}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <Space>
              <KeyOutlined />
              API Tokens
            </Space>
          }
          key="api-tokens"
        >
          <div className="configuration-tab-content">
            <ApiTokensTab buildAuthHeaders={buildAuthHeaders} />
          </div>
        </TabPane>

        {isAdmin && (
          <TabPane 
            tab={
              <Space>
                <TeamOutlined />
                User Management
              </Space>
            } 
            key="users"
          >
            <div className="configuration-tab-content configuration-tab-users">
              <UserManagementTab
                isAdmin={isAdmin}
                isMaster={isMaster}
                companyMembers={companyMembers}
                invitations={invitations}
                userColumns={userColumns}
                invitationColumns={invitationColumns}
                inviteModalVisible={inviteModalVisible}
                setInviteModalVisible={setInviteModalVisible}
                inviteForm={inviteForm}
                handleInviteUser={handleInviteUser}
                roles={roles}
                loading={loading}
                getRoleIcon={getRoleIcon}
              />
            </div>
          </TabPane>
        )}

        {(isAdmin || isMaster) && (
          <TabPane 
            tab={
              <Space>
                <BuildOutlined />
                Company
              </Space>
            } 
            key="company"
          >
          <div className="configuration-tab-content configuration-tab-company">
            <CompanyManagementTab
              isAdmin={isAdmin}
              isMaster={isMaster}
              companyName={effectiveCompanyName}
              companyId={currentCompanyId}
              buildAuthHeaders={buildAuthHeaders}
              onAuthMismatch={handleAuthMismatch}
            />
          </div>
        </TabPane>
      )}

      {isAdmin && (
        <TabPane
          tab={
            <Space>
              <SlidersOutlined />
              Ranges
            </Space>
          }
          key="ranges"
        >
          <div className="configuration-tab-content configuration-tab-ranges">
            <RangesTab
              isAdmin={isAdmin}
              companyId={currentCompanyId}
              buildAuthHeaders={buildAuthHeaders}
              onAuthMismatch={handleAuthMismatch}
            />
          </div>
        </TabPane>
      )}

        {(isAdmin || isMaster) && (
          <TabPane
            tab={
              <Space>
                <CreditCardOutlined />
                Subscription
              </Space>
            }
            key="subscription"
          >
            <div className="configuration-tab-content configuration-tab-subscription">
              <SubscriptionTab
                isAdmin={isAdmin}
                isMaster={isMaster}
                companyId={currentCompanyId}
                buildAuthHeaders={buildAuthHeaders}
                onAuthMismatch={handleAuthMismatch}
              />
            </div>
          </TabPane>
        )}

        {/* Show for users who might want to create their own company */}
        {!isAdmin && !isMaster && (
          <TabPane 
            tab={
              <Space>
                <PlusOutlined />
                New Company
              </Space>
            } 
            key="new-company"
          >
            <div className="configuration-tab-content configuration-tab-new-company">
              <NewCompanyTab />
            </div>
          </TabPane>
        )}
      </Tabs>
    </div>
  );
};

export default Configuration;
